import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { ConfirmarHoja } from '#components/mobile/ui/ConfirmarHoja';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, texto } from '#components/mobile/ui/tokens';

import {
  borrarDatosLocales,
  esFallo,
  mensajeFallo,
  pedirBorradoCuenta,
} from './borrarCuenta';
import type { FalloBorrado } from './borrarCuenta';

/**
 * Ajustes → Tus datos → «Borrar mi cuenta y mis datos»: qué se borra, el
 * botón rojo y la hoja de confirmación. Si el servidor lo borra, se limpia
 * este dispositivo y se vuelve a la pantalla de entrada.
 */
export function BorrarCuentaSettings() {
  const { t } = useTranslation();
  const [abierta, setAbierta] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [fallo, setFallo] = useState<FalloBorrado | null>(null);

  const cerrar = () => {
    if (borrando) {
      return;
    }
    setAbierta(false);
    setFallo(null);
  };

  const confirmar = async () => {
    setBorrando(true);
    setFallo(null);
    const resultado = await pedirBorradoCuenta();
    if (esFallo(resultado)) {
      setFallo(resultado);
      setBorrando(false);
      return;
    }
    await borrarDatosLocales();
    window.location.replace('/');
  };

  return (
    <View style={{ gap: 12 }}>
      <Text style={{ ...texto.secundario, color: color.fg2, lineHeight: 1.45 }}>
        <Trans>
          Deletes your account on the Rumbo server, the budgets you own (people
          you share them with lose access), your bank keys and your usage data.
          Budgets that other people share with you stay with them.
        </Trans>
      </Text>
      <Boton
        bloque
        variante="tonal"
        data-testid="borrar-cuenta"
        onPress={() => setAbierta(true)}
      >
        <Trans>Delete my account and my data</Trans>
      </Boton>
      {abierta && (
        <Hoja
          aria-label={t('Delete my account and my data')}
          data-testid="hoja-borrar-cuenta"
          onCerrar={cerrar}
        >
          <ConfirmarHoja
            icono="trash"
            pregunta={t('Delete your account and all your data?')}
            detalle={t(
              'This cannot be undone. If you want to keep a budget, download a backup first.',
            )}
            textoAccion={
              borrando ? t('Deleting…') : t('Delete my account and my data')
            }
            accionDeshabilitada={borrando}
            testIdAccion="confirmar-borrar-cuenta"
            onConfirmar={() => void confirmar()}
            onCancelar={cerrar}
            onCerrar={cerrar}
          >
            {fallo && (
              <Text
                role="alert"
                style={{ color: color.bad, fontSize: 14, lineHeight: 1.4 }}
              >
                {mensajeFallo(t, fallo)}
              </Text>
            )}
          </ConfirmarHoja>
        </Hoja>
      )}
    </View>
  );
}
